import { useEffect, useMemo, useState } from "react";
import apiService, { handleError } from "../../../Common/ApiCommon/ApiService";

const useAddGameTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [tableHistoryData, setTableHistoryData] = useState([]);
  const [tableNames, setTableNames] = useState([]); // To hold unique table names
  const [selectedFilter, setSelectedFilter] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [averageData, setAverageData] = useState([]);

  const formatDate = (date) => {
    if (!date) return null;
    return date.toString();
  };

  const getDateRange = (filter) => {
    const today = new Date();
    let fromDate, toDate;

    if (filter === "today") {
      fromDate = toDate = today;
    } else if (filter === "weekly") {
      fromDate = new Date(today);
      fromDate.setDate(today.getDate() - 7);
      toDate = today;
    } else if (filter === "monthly") {
      fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
      toDate = today;
    }

    return {
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    };
  };

  const fetchTableHistory = async () => {
    try {
      let filterQuery = "";
      let from_date = fromDate;
      let to_date = toDate;

      if (
        selectedFilter === "today" ||
        selectedFilter === "weekly" ||
        selectedFilter === "monthly"
      ) {
        const range = getDateRange(selectedFilter);
        from_date = range.fromDate;
        to_date = range.toDate;
      } else if (selectedFilter === "custom" && fromDate && toDate) {
        from_date = formatDate(fromDate);
        to_date = formatDate(toDate);
      }

      filterQuery = `&date_filter=${selectedFilter}&start_date=${from_date}&end_date=${to_date}`;

      const response = await apiService.get(`/reports/table_history?${filterQuery}`);

      // Extract unique table names from API response
      const uniqueTableNames = new Set();
      response.data.forEach((item) => {
        item.tables.forEach((table) => {
          uniqueTableNames.add(table.table_name);
        });
      });
      setTableNames(Array.from(uniqueTableNames));
      setAverageData(response.avg_data);
      // Format data for the table
      const formattedData = response.data.map((item) => {
        const row = {
          date: item.date,
        };

        item.tables.forEach((table) => {
          row[table.table_name] = table.today_total_amount;
          row[`${table.table_name}_Runtime`] = table.total_duration;
        });

        row.totalCollection = `₹ ${item.today_total_collection}`;
        return row;
      });

      setTableHistoryData(formattedData);
    } catch (errors) {
      handleError(errors);
    }
  };

  useEffect(() => {
    fetchTableHistory();
  }, [selectedFilter, fromDate, toDate]);

  // Dynamically create columns based on tableNames
  const columns = useMemo(() => {
    const tableColumns = tableNames.map((tableName) => {
      const avgEntry = averageData.find((item) => item.table_name === tableName);

      return {
        Header: () => (
          <div className="flex flex-col items-center">
            <div>{tableName}</div>
            {avgEntry && (
              <div className="text-xs text-gray-500">₹ {avgEntry.total_amount}</div>
            )}
          </div>
        ),
        accessor: tableName,
        Cell: ({ row }) => {
          const amount = row.original[tableName] || 0;
          const runtime = row.original[`${tableName}_Runtime`];
          return (
            <div>
              <div className="">{`₹ ${amount}`}</div>
              {runtime ? (
                <div className="text-xs text-gray-500 text-nowrap">({runtime})</div>
              ) : null}
            </div>
          );
        },
      };
    });

    return [
      {
        Header: "Collection",
        columns: [{ Header: "Date", accessor: "date" }, ...tableColumns],
      },
      {
        Header: "Total Collection",
        columns: [{ Header: "Total", accessor: "totalCollection" }],
      },
    ];
  }, [tableNames, averageData]);

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = tableHistoryData.slice(indexOfFirstRow, indexOfLastRow);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  return {
    columns,
    currentRows,
    totalRows: tableHistoryData.length,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    selectedFilter,
    setSelectedFilter,
    setFromDate,
    setToDate,
    fromDate,
    toDate, tableHistoryData, tableNames,averageData
  };
};

export default useAddGameTable;
